import * as React from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import { LinearProgress, Pagination } from '@mui/material';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { useQuery } from 'react-query';
import BaseTableHead from './parts/BaseTableHead';
import setTableCellType from 'utils/setTableCellType';
import InfoActionButton from '../action-buttons/InfoActionButton';
import TableColumnToggleDrawer from 'components/TableColumnToggleDrawer';
import useColumnToggle from 'hooks/useColumnToggle';
import CONTRACT_STATUS_TYPE from 'shared/contractStatusTypeList';
import addActiveInTableCell from 'utils/addActiveInTableCell';
import { useTranslation } from 'react-i18next';
import { clientTypeVariants } from 'shared/tableColVariantsList';

export default function ContractTable({
  dataPath = '',
  tableName = '',
  emitTableColumns = {},
  emitRefetch = {},
  actionInfo = null,
  actionCustomInfo = null,
  noActions = false,
  customSearch = '',
  data,
}) {
  const { t } = useTranslation();
  const { search } = useLocation();
  const axiosPrivate = useAxiosPrivate();
  const headCells = [
    {
      code: 'name',
      label: t('common.table.contractShort'),
      sortable: true,
      width: 110,
    },
    {
      code: 'homes',
      label: t('common.table.homeShort'),
      sortable: true,
      width: 70,
    },
    { code: 'custom', label: t('common.table.custom'), sortable: true },
    {
      code: 'square',
      label: t('common.table.square'),
      sortable: true,
      numeric: true,
    },
    {
      code: 'sum',
      label: t('common.table.sumShort'),
      sortable: true,
      numeric: true,
    },
    {
      code: 'start_price',
      label: t('common.table.startPrice'),
      sortable: true,
      numeric: true,
    },
    {
      code: 'peniya',
      label: t('common.table.penalty'),
      isActive: false,
      sortable: true,
      numeric: true,
    },
    { code: 'date', label: t('common.table.date'), sortable: true },
    { code: 'staff', label: t('common.table.staff') },
    {
      code: 'client_type',
      label: t('common.table.clientType'),
    },
  ];
  const columns = [
    { code: 'name', type: 'strong' },
    { code: 'homes', type: 'nested', childStr: 'number' },
    {
      code: 'custom',
      type: 'nestedChain',
      childStrings: ['surname', 'name', 'middlename'],
    },
    { code: 'square', type: 'area' },
    { code: 'sum', type: 'priceCurrency' },
    { code: 'start_price', type: 'priceCurrency' },
    { code: 'peniya', type: 'priceCurrency', isActive: false },
    { code: 'date', type: 'date' },
    { code: 'staff', type: 'nested', childStr: 'name' },
    {
      code: 'client_type',
      type: 'customStatus',
      variants: clientTypeVariants,
    },
  ];
  const { tableData, tableHeadCells, tableColumnCells, setTableHeadCells } =
    useColumnToggle(
      tableName,
      addActiveInTableCell(headCells),
      addActiveInTableCell(columns)
    );

  const [hasError, setHasError] = React.useState(false);
  const {
    error,
    data: rows,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: [dataPath + 'Key', search, customSearch],
    queryFn: async function () {
      const response = await axiosPrivate.get(
        `/${dataPath}${customSearch || search || ''}`
      );
      return response?.data?.data;
    },
    onSettled: () => {
      emitRefetch.setRefetch(false);
    },
    enabled: !hasError && !!dataPath,
    onError: (error) => {
      setHasError(true);
    },
    retry: false,
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [page, setPage] = React.useState(
    (searchParams.get('page') && parseInt(searchParams.get('page'))) || 1
  );

  const toViewContract = (id) => {
    navigate(`/admin/contract/view/${id}`);
  };

  // React.useEffect(() => {
  //   if (queryPath && queryPath.length > 0) {
  //     refetch();
  //   }
  // }, [queryPath]);

  // React.useEffect(() => {
  //   createQueryPath();
  // }, [searchParams]);

  React.useEffect(() => {
    if (emitRefetch && emitRefetch.refetch) {
      refetch();
    }
  }, [emitRefetch.refetch]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    searchParams.set('page', newPage);
    setSearchParams(searchParams);
  };

  // const createQueryPath = () => {
  //   let newQueryPath = `/${dataPath}`;
  //   const localSearchParams = Object.fromEntries([...searchParams]);
  //   Object.keys(localSearchParams).forEach((item, index) => {
  //     if (index == 0) {
  //       newQueryPath += `?${item}=${localSearchParams[item]}`;
  //     } else {
  //       newQueryPath += `&${item}=${localSearchParams[item]}`;
  //     }
  //   });
  //   setQueryPath(newQueryPath);
  //   if (!isNaN(localSearchParams.page)) {
  //     setPage(parseInt(localSearchParams.page));
  //   }
  // };

  return (
    <Box className='base-table w-full h-full flex flex-col'>
      <TableContainer className='flex-auto h-full'>
        <Table
          stickyHeader
          sx={{ minWidth: 750, height: 'max-content' }}
          aria-labelledby='tableTitle'
        >
          {tableHeadCells && tableColumnCells && (
            <React.Fragment>
              <BaseTableHead headCells={tableHeadCells} noActions={noActions} />
              {isLoading || isFetching ? (
                <TableBody className='overflow-hidden'>
                  <TableRow>
                    <TableCell colSpan={tableHeadCells.length + 1}>
                      <LinearProgress />
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : isError ? (
                <TableBody className='overflow-hidden'>
                  <TableRow>
                    <TableCell colSpan={tableHeadCells.length + 1}>
                      <div className='flex flex-col items-center'>
                        {error?.response?.data?.message && (
                          <span className='text-red-600 font-medium'>
                            {error?.response?.data?.message}
                          </span>
                        )}
                        {error?.response?.data?.details &&
                          error?.response?.data?.details[0]?.message && (
                            <div>
                              <span className='text-red-600 font-medium'>
                                {t('common.errors.message')}
                              </span>
                              <span>
                                {error?.response?.data?.details[0]?.message}
                              </span>
                            </div>
                          )}
                      </div>
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : (rows && rows.data && rows.data.length > 0) ||
                data?.length ? (
                <React.Fragment>
                  <TableBody className='overflow-hidden'>
                    {dataPath
                      ? rows.data.map((row, rowIndex) => {
                          return (
                            <TableRow
                              hover
                              tabIndex={-1}
                              key={'row-' + rowIndex}
                              className={
                                (row?.status ==
                                CONTRACT_STATUS_TYPE.COMPLETE.code
                                  ? 'is-completed !bg-[#EFFBF2] hover:!bg-[#DFF5E7] hover:after:!bg-[#23C840]'
                                  : row?.status ==
                                    CONTRACT_STATUS_TYPE.CANCEL.code
                                  ? 'is-cancelled !bg-[#FAF0F0] hover:!bg-[#F6E2E2] hover:after:!bg-[#FF6D6D]'
                                  : '') + ' cursor-pointer'
                              }
                              onClick={() => toViewContract(row?.id)}
                            >
                              {tableColumnCells.map((column, columnIndex) => (
                                <TableCell
                                  align={
                                    tableHeadCells[columnIndex]?.numeric
                                      ? 'right'
                                      : 'left'
                                  }
                                  key={'column-' + columnIndex}
                                >
                                  {column.type
                                    ? setTableCellType(
                                        column,
                                        row[column.code],
                                        row
                                      )
                                    : row[column.code]}
                                </TableCell>
                              ))}

                              {noActions ? null : (
                                <TableCell align='right'>
                                  {actionInfo && (
                                    <InfoActionButton
                                      link={`/view/${row.id}`}
                                    />
                                  )}
                                  {actionCustomInfo && (
                                    <InfoActionButton
                                      link={`${actionCustomInfo.baseLink}/${
                                        row[actionCustomInfo.linkIdCode]
                                      }`}
                                    />
                                  )}
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })
                      : data.map((row, rowIndex) => {
                          return (
                            <TableRow
                              hover
                              tabIndex={-1}
                              key={'row-' + rowIndex}
                              className={
                                (row?.status ==
                                CONTRACT_STATUS_TYPE.COMPLETE.code
                                  ? 'is-completed !bg-[#EFFBF2] hover:!bg-[#DFF5E7] hover:after:!bg-[#23C840]'
                                  : row?.status ==
                                    CONTRACT_STATUS_TYPE.CANCEL.code
                                  ? 'is-cancelled !bg-[#FAF0F0] hover:!bg-[#F6E2E2] hover:after:!bg-[#FF6D6D]'
                                  : '') + ' cursor-pointer'
                              }
                              onClick={() => toViewContract(row?.id)}
                            >
                              {tableColumnCells.map((column, columnIndex) => (
                                <TableCell
                                  align={
                                    tableHeadCells[columnIndex]?.numeric
                                      ? 'right'
                                      : 'left'
                                  }
                                  key={'column-' + columnIndex}
                                >
                                  {column.type
                                    ? setTableCellType(
                                        column,
                                        row[column.code],
                                        row
                                      )
                                    : row[column.code]}
                                </TableCell>
                              ))}

                              {noActions ? null : (
                                <TableCell align='right'>
                                  {actionInfo && (
                                    <InfoActionButton
                                      link={`/view/${row.id}`}
                                    />
                                  )}
                                  {actionCustomInfo && (
                                    <InfoActionButton
                                      link={`${actionCustomInfo.baseLink}/${
                                        row[actionCustomInfo.linkIdCode]
                                      }`}
                                    />
                                  )}
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })}
                  </TableBody>
                </React.Fragment>
              ) : (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={tableHeadCells.length + 1}>
                      <span className='no-data-found-wrapper'>
                        <i className='bi bi-exclamation-octagon text-xl leading-4 mr-1' />{' '}
                        {t('common.global.noDataFound')}
                      </span>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </React.Fragment>
          )}
        </Table>
      </TableContainer>
      {rows && rows.data && rows.data.length > 0 && (
        <div className='p-3 mb-2 mt-4 flex items-center justify-between gap-3 rounded-lg !shadow-none border border-line bg-white'>
          <div className='text-[13px] text-muted whitespace-nowrap'>
            {t('common.table.total')}:{' '}
            <span className='font-medium text-ink'>{rows?.total || '0'}</span>
          </div>
          <Pagination
            count={Math.ceil(rows.total / rows.per_page) || 1}
            page={page}
            onChange={handleChangePage}
            shape='rounded'
            color='primary'
            showFirstButton
            showLastButton
          />
        </div>
      )}

      {emitTableColumns && Object.keys(emitTableColumns).length == 2 && (
        <TableColumnToggleDrawer
          open={emitTableColumns.open}
          setOpen={emitTableColumns.setOpen}
          tableData={tableData}
          setTableHeadCells={setTableHeadCells}
        />
      )}
    </Box>
  );
}
